import React, { createContext, useState, useEffect, ReactNode } from 'react';
import { subscriptionService } from '../services/SubscriptionService';
import { apiFetch, getAuthToken as getStoredToken } from '../services/httpClient';

export type AccessMode = 'preview' | 'trial' | 'active' | 'read_only' | 'blocked';

interface User {
  id: string;
  email: string;
  role: 'admin' | 'manager' | 'user';
  name: string;
  userId: string;
  companyName?: string;
  subscriptionType: 'standard' | 'premium';
  subscriptionEndDate?: string | null;
  subscriptionStatus?: string | null;
  isBeta?: boolean;
}

interface AccessInfo {
  access_mode: AccessMode;
  effective_plan: 'standard' | 'premium' | null;
  ends_at: string | null;
  grace_ends_at: string | null;
  trial_used: boolean;
}

interface AuthContextType {
  user: User | null;
  access: AccessInfo | null;
  loading: boolean;
  error: string | null;
  login: (email: string, password: string) => Promise<void>;
  register: (email: string, password: string, name: string) => Promise<void>;
  requestPasswordReset: (email: string) => Promise<void>;
  resetPassword: (token: string, password: string) => Promise<void>;
  startTrial: () => Promise<AccessInfo | null>;
  logout: () => void;
  isAuthenticated: boolean;
  getAuthToken: () => string | null;
  refreshUser: () => Promise<User | null>;
}

const defaultAuthContext: AuthContextType = {
  user: null,
  access: null,
  loading: false,
  error: null,
  login: async () => {},
  register: async () => {},
  requestPasswordReset: async () => {},
  resetPassword: async () => {},
  startTrial: async () => null,
  logout: () => {},
  isAuthenticated: false,
  getAuthToken: () => null,
  refreshUser: async () => null,
};

export const AuthContext = createContext<AuthContextType>(defaultAuthContext);

interface AuthProviderProps {
  children: ReactNode;
}

export const AuthProvider: React.FC<AuthProviderProps> = ({ children }) => {
  const [user, setUser] = useState<User | null>(null);
  const [access, setAccess] = useState<AccessInfo | null>(null);
  const [loading, setLoading] = useState<boolean>(true);
  const [error, setError] = useState<string | null>(null);

  const mapUserFromApi = (apiUser: any): User => ({
    id: apiUser.id || apiUser.userId,
    email: apiUser.email,
    role: apiUser.role || 'user',
    name: apiUser.name,
    userId: apiUser.userId,
    companyName: apiUser.companyName,
    subscriptionType: apiUser.subscription_type === 'premium' ? 'premium' : 'standard',
    subscriptionEndDate: apiUser.subscription_end_date || null,
    subscriptionStatus: apiUser.subscription_status || null,
    isBeta: !!apiUser.is_beta,
  });

  const mapAccessFromApi = (apiAccess: any): AccessInfo | null => {
    if (!apiAccess) return null;
    return {
      access_mode: apiAccess.access_mode,
      effective_plan: apiAccess.effective_plan,
      ends_at: apiAccess.ends_at,
      grace_ends_at: apiAccess.grace_ends_at,
      trial_used: !!apiAccess.trial_used,
    };
  };

  // Vérifier si l'utilisateur est déjà connecté au chargement
  useEffect(() => {
    const checkAuthStatus = async () => {
      try {
        const storedUserRaw = localStorage.getItem('user');
        const storedToken = localStorage.getItem('authToken');
        const storedAccessRaw = localStorage.getItem('access');

        if (storedUserRaw && storedToken) {
          const refreshed = await refreshUser();
          if (!refreshed) {
            try {
              const parsedUser = JSON.parse(storedUserRaw);
              const normalizedUser: User = {
                ...parsedUser,
                subscriptionType: parsedUser.subscriptionType === 'premium' ? 'premium' : 'standard',
                subscriptionEndDate: parsedUser.subscriptionEndDate || null,
              };
              setUser(normalizedUser);
              if (storedAccessRaw) {
                setAccess(JSON.parse(storedAccessRaw));
              }
              subscriptionService.setAuthUser(normalizedUser);
            } catch (parseError) {
              console.error("Erreur lors du chargement de l'utilisateur local", parseError);
            }
          }
        }
      } catch (err) {
        console.error("Erreur lors de la vérification de l'authentification:", err);
      } finally {
        setLoading(false);
      }
    };

    checkAuthStatus();
  }, []);

  const login = async (email: string, password: string) => {
    setLoading(true);
    setError(null);
    try {
      const data = await apiFetch<any>('/auth/login', {
        method: 'POST',
        body: JSON.stringify({ email, password }),
      });

      if (data.success && data.user && data.token) {
        const userData: User = mapUserFromApi(data.user);
        const accessData = mapAccessFromApi(data.access);

        localStorage.setItem('authToken', data.token);
        localStorage.setItem('user', JSON.stringify(userData));
        localStorage.setItem('access', JSON.stringify(accessData));
        subscriptionService.setAuthUser(userData);
        setUser(userData);
        setAccess(accessData);
      } else {
        throw new Error("Réponse d'authentification invalide");
      }
    } catch (err) {
      if (err instanceof Error) {
        setError(err.message);
      } else {
        setError('Une erreur est survenue lors de la connexion');
      }
      throw err;
    } finally {
      setLoading(false);
    }
  };

  const register = async (email: string, password: string, name: string) => {
    setLoading(true);
    setError(null);
    try {
      const userId = `user-${Date.now()}`;
      const token = 'dev-token';

      const userData: User = {
        id: '3',
        email,
        role: 'user',
        name,
        userId,
        companyName: 'Nouvelle Entreprise',
        subscriptionType: 'standard',
        subscriptionEndDate: null,
      };

      localStorage.setItem('authToken', token);
      localStorage.setItem('user', JSON.stringify(userData));
      localStorage.setItem('access', JSON.stringify({ access_mode: 'preview', effective_plan: null, ends_at: null, grace_ends_at: null, trial_used: false }));
      subscriptionService.setAuthUser(userData);
      setUser(userData);
      setAccess({
        access_mode: 'preview',
        effective_plan: null,
        ends_at: null,
        grace_ends_at: null,
        trial_used: false,
      });
    } catch (err) {
      if (err instanceof Error) {
        setError(err.message);
      } else {
        setError("Une erreur est survenue lors de l'inscription");
      }
      throw err;
    } finally {
      setLoading(false);
    }
  };

  const requestPasswordReset = async (email: string) => {
    setLoading(true);
    setError(null);

    try {
      await apiFetch('/auth/password/forgot', {
        method: 'POST',
        body: JSON.stringify({ email }),
      });
    } catch (err) {
      if (err instanceof Error) {
        setError(err.message);
      } else {
        setError('Une erreur est survenue lors de la demande de reinitialisation');
      }
      throw err;
    } finally {
      setLoading(false);
    }
  };

  const resetPassword = async (token: string, password: string) => {
    setLoading(true);
    setError(null);

    try {
      await apiFetch('/auth/password/reset', {
        method: 'POST',
        body: JSON.stringify({ token, password }),
      });
    } catch (err) {
      if (err instanceof Error) {
        setError(err.message);
      } else {
        setError('Une erreur est survenue lors de la reinitialisation');
      }
      throw err;
    } finally {
      setLoading(false);
    }
  };

  const startTrial = async (): Promise<AccessInfo | null> => {
    const storedToken = localStorage.getItem('authToken');
    if (!storedToken) return null;
    try {
      const data = await apiFetch<any>('/subscription/start-trial', {
        method: 'POST',
        headers: {
          Authorization: `Bearer ${storedToken}`,
        },
      });
      if (data.user) {
        const mappedUser = mapUserFromApi(data.user);
        const mappedAccess = mapAccessFromApi(data.access);
        setUser(mappedUser);
        setAccess(mappedAccess);
        localStorage.setItem('user', JSON.stringify(mappedUser));
        localStorage.setItem('access', JSON.stringify(mappedAccess));
        subscriptionService.setAuthUser(mappedUser);
        return mappedAccess;
      }
    } catch (err) {
      if (err instanceof Error) {
        setError(err.message);
      }
      throw err;
    }
    return null;
  };

  // Fonction pour obtenir le token d'authentification
  const getAuthToken = (): string | null => getStoredToken();

  const refreshUser = async (): Promise<User | null> => {
    const storedToken = localStorage.getItem('authToken');
    if (!storedToken) {
      setUser(null);
      setAccess(null);
      return null;
    }

    try {
      const data = await apiFetch<any>('/auth/verify', {
        method: 'GET',
        headers: {
          Authorization: `Bearer ${storedToken}`,
        },
      });
      if (data.success && data.user) {
        const mappedUser = mapUserFromApi(data.user);
        const mappedAccess = mapAccessFromApi(data.access);
        localStorage.setItem('user', JSON.stringify(mappedUser));
        localStorage.setItem('access', JSON.stringify(mappedAccess));
        subscriptionService.setAuthUser(mappedUser);
        setUser(mappedUser);
        setAccess(mappedAccess);
        return mappedUser;
      }
    } catch (verifyError) {
      console.warn('Impossible de verifier le token', verifyError);
    }

    localStorage.removeItem('user');
    localStorage.removeItem('authToken');
    localStorage.removeItem('access');
    setUser(null);
    setAccess(null);
    return null;
  };

  const logout = () => {
    setUser(null);
    setAccess(null);
    localStorage.removeItem('user');
    localStorage.removeItem('authToken');
    localStorage.removeItem('access');
    localStorage.removeItem('lastSubscriptionType');
    subscriptionService.setAuthUser(null);
  };

  return (
    <AuthContext.Provider
      value={{
        user,
        access,
        loading,
        error,
        login,
        register,
        logout,
        isAuthenticated: !!user,
        getAuthToken,
        requestPasswordReset,
        resetPassword,
        refreshUser,
        startTrial,
      }}
    >
      {children}
    </AuthContext.Provider>
  );
};

export default AuthContext;
